import ValidationMessages from './validationMessageConfig'

const MIN_DURATION = 0.25
const MAX_DURATION = 16

const FIELDS = ['vacation', 'float', 'flexible', 'other'] as const
type Field = (typeof FIELDS)[number]

// Helper interface for time objects
interface TimeOption {
  hour?: { label?: string; value?: string }
  minute?: { label?: string; value?: string }
  ampm?: { label?: string; value?: string }
}

function toNum(inputValue: unknown): number {
  const numericString =
    typeof inputValue === 'string' || typeof inputValue === 'number'
      ? String(inputValue)
      : ''
  const parsedNumber = Number.parseFloat(numericString)
  return Number.isNaN(parsedNumber) ? 0 : parsedNumber
}

// Helper: Convert time object to minutes since midnight (24-hour format)
function timeToMinutes(timeObj: TimeOption): number | null {
  if (!timeObj.hour || !timeObj.minute || !timeObj.ampm) {
    return null
  }

  let hour = Number.parseInt(timeObj.hour.value || '0', 10)
  const minute = Number.parseInt(timeObj.minute.value || '0', 10)
  const ampm = timeObj.ampm.value || ''

  // Convert to 24-hour format
  if (ampm === 'PM' && hour !== 12) {
    hour += 12
  } else if (ampm === 'AM' && hour === 12) {
    hour = 0
  }

  return hour * 60 + minute
}

// Helper: Validate partial day times
function validatePartialDayTimes(
  fromTime: TimeOption,
  toTime: TimeOption,
  messages: string[]
): void {
  const fromMinutes = timeToMinutes(fromTime)
  const toMinutes = timeToMinutes(toTime)

  // Only validate if both times are selected
  if (fromMinutes === null || toMinutes === null) {
    return
  }

  if (fromMinutes >= toMinutes) {
    messages.push(ValidationMessages.PARTIAL_DAY_TIME_ORDER)
  }
}

// Helper: Validate that hours entered don't exceed partial day time duration
function validatePartialDayHoursDuration(
  fromTime: TimeOption,
  toTime: TimeOption,
  hoursEntered: Record<string, number>,
  messages: string[],
  nextFieldErrors: Record<string, string>
): void {
  const fromMinutes = timeToMinutes(fromTime)
  const toMinutes = timeToMinutes(toTime)

  // Only validate if both times are selected and valid
  if (fromMinutes === null || toMinutes === null || fromMinutes >= toMinutes) {
    return
  }

  // Calculate duration in hours
  const durationMinutes = toMinutes - fromMinutes
  const durationHours = durationMinutes / 60

  // Check if any field hours exceed the duration
  const totalHours = Object.values(hoursEntered).reduce(
    (sum, hours) => sum + hours,
    0
  )

  if (totalHours > durationHours) {
    messages.push(ValidationMessages.PARTIAL_DAY_HOURS_EXCEED_DURATION)
    // Flag every field the user actually entered hours into so the inline
    // 'Revise' marker shows next to each one, not just the banner.
    for (const [field, hours] of Object.entries(hoursEntered)) {
      if (hours > 0) {
        nextFieldErrors[field] = 'Revise'
      }
    }
  }
  // Also validate when total hours is less than the selected partial-day duration
  const hasSomeHours = totalHours > 0
  if (hasSomeHours && totalHours < durationHours) {
    // Primary banner header describing the issue, followed by a short
    // instruction line to prompt the user to revise the request.
    messages.push(ValidationMessages.PARTIAL_DAY_HOURS_LESS_THAN_DURATION)
    for (const [field, hours] of Object.entries(hoursEntered)) {
      if (hours > 0) {
        nextFieldErrors[field] = 'Revise'
      }
    }
  }
}

// Helper: Check field-level duration and balance constraints
function validateFieldInline(
  hoursEntered: number,
  availableBalance: number
): string | null {
  if (hoursEntered > 0 && hoursEntered > availableBalance)
    return 'exceeds balance'
  if (hoursEntered > 0 && hoursEntered < MIN_DURATION) return 'exceeds time'
  if (hoursEntered > MAX_DURATION) return 'exceeds time'
  return null
}

// Helper: Validate vacation rules
function validateVacationRules(
  vacationHours: number,
  messages: string[]
): void {
  const isOutOfRange =
    vacationHours > MAX_DURATION ||
    (vacationHours > 0 && vacationHours < MIN_DURATION)
  if (isOutOfRange) {
    messages.push(ValidationMessages.DURATION)
  }
}

// Helper: Validate flexible rules
function validateFlexibleRules(
  flexibleHours: number,
  messages: string[],
  fieldErrors?: Record<string, string>
): void {
  if (flexibleHours > MAX_DURATION) {
    messages.push(ValidationMessages.FLEXIBLE_DURATION)
    if (fieldErrors) {
      fieldErrors['flexible'] = 'exceeds time'
    }
  }

  const requiresMinTwoHours = flexibleHours > 0 && flexibleHours < 2
  if (requiresMinTwoHours) {
    messages.push(ValidationMessages.FLEXIBLE_MIN_TWO_HOURS)
    if (fieldErrors) {
      fieldErrors['flexible'] = 'exceeds time'
    }
  }
}

// Helper: Validate float rules
function validateFloatRules(floatHours: number, messages: string[]): void {
  const isOutOfRange =
    floatHours > MAX_DURATION || (floatHours > 0 && floatHours < MIN_DURATION)
  if (isOutOfRange) {
    messages.push(ValidationMessages.FLOAT_DURATION)
  }
}

// Helper: Validate other type rules
function validateOtherRules(
  otherHours: number,
  otherSubtype: string | undefined,
  messages: string[],
  nextFieldErrors: Record<string, string>
): void {
  // Duration check: other hours must be >= MIN_DURATION and <= MAX_DURATION
  const isDurationInvalid =
    otherHours > MAX_DURATION || (otherHours > 0 && otherHours < MIN_DURATION)
  if (isDurationInvalid) {
    messages.push(ValidationMessages.OTHER_DURATION)
  }

  // Subtype must always be selected when other hours > 0, regardless of duration.
  // Uses a distinct 'otherSubtype' key (not 'other', which is the hours field's
  // own error) so this snapshots at validation time instead of being recomputed
  // live on every keystroke — it should only ever appear alongside the banner.
  if (otherHours > 0 && !otherSubtype) {
    messages.push(ValidationMessages.OTHER_SUBTYPE_REQUIRED)
    nextFieldErrors['otherSubtype'] = 'Required'
  }
}

// Helper: Validate that total hours across all leave types don't exceed max duration per day
function validateTotalHoursPerDay(
  normalizedHours: Record<string, number>,
  messages: string[]
): void {
  const totalHours = Object.values(normalizedHours).reduce(
    (sum, hours) => sum + hours,
    0
  )
  if (totalHours > 16) {
    messages.push(ValidationMessages.TOTAL_HOURS_EXCEED_MAX)
  }
}

// Helper: Collect balance errors
function collectBalanceErrors(
  fieldErrors: Record<string, string>,
  messages: string[]
): void {
  const flexibleExceedsBalance = fieldErrors['flexible'] === 'exceeds balance'
  const floatExceedsBalance = fieldErrors['float'] === 'exceeds balance'
  const otherFieldExceedsBalance =
    fieldErrors['vacation'] === 'exceeds balance' ||
    fieldErrors['other'] === 'exceeds balance'

  if (flexibleExceedsBalance) {
    messages.push(ValidationMessages.FLEXIBLE_BALANCE)
  }
  if (floatExceedsBalance) {
    messages.push(ValidationMessages.FLOAT_BALANCE)
  }
  if (otherFieldExceedsBalance) {
    messages.push(ValidationMessages.BALANCE)
  }
}

export function validateEntries(
  entries: Record<string, string> = {},
  availableBalances: Record<string, number | undefined> = {},
  leaveTypeSelected: Record<string, string> = {},
  selectedDuration = 'Full Day',
  requestType?: string,
  defaultAvailableBalance = 60,

  partialDayTimes?: { from: TimeOption; to: TimeOption }
) {
  const fieldErrors: Record<string, string> = {}
  const errorMessages: string[] = []

  // Determine which fields to validate
  const fieldsToCheck: ReadonlyArray<Field> =
    requestType === 'vacation-bidding'
      ? (['vacation'] as ReadonlyArray<Field>)
      : FIELDS

  // Normalize numeric values for each field
  const normalizedHours = Object.fromEntries(
    fieldsToCheck.map((fieldName) => [fieldName, toNum(entries[fieldName])])
  ) as Record<string, number>

  // Validate each field's inline constraints
  fieldsToCheck.forEach((fieldName) => {
    const hoursEntered = normalizedHours[fieldName]
    const availableBalance =
      availableBalances[fieldName] ?? defaultAvailableBalance
    const validationError = validateFieldInline(hoursEntered, availableBalance)
    if (validationError) {
      fieldErrors[fieldName] = validationError
    }
  })

  // Validate field-specific business rules
  if (fieldsToCheck.includes('vacation')) {
    validateVacationRules(normalizedHours['vacation'] || 0, errorMessages)
  }

  if (fieldsToCheck.includes('float')) {
    validateFloatRules(normalizedHours['float'] || 0, errorMessages)
  }

  if (fieldsToCheck.includes('flexible')) {
    validateFlexibleRules(
      normalizedHours['flexible'] || 0,
      errorMessages,
      fieldErrors
    )
  }

  if (fieldsToCheck.includes('other')) {
    validateOtherRules(
      normalizedHours['other'] || 0,
      leaveTypeSelected['other'],
      errorMessages,
      fieldErrors
    )
  }

  // Validate that total hours across all leave types don't exceed 16 hours per day
  validateTotalHoursPerDay(normalizedHours, errorMessages)

  // Collect balance-related errors
  collectBalanceErrors(fieldErrors, errorMessages)

  // Validate minimum hours requirement for full day and partial day requests
  const totalHoursEntered = fieldsToCheck.reduce(
    (sum, fieldName) => sum + (normalizedHours[fieldName] || 0),
    0
  )
  const isRequestWithNoHours =
    (selectedDuration === 'Full Day' || selectedDuration === 'Partial Day') &&
    totalHoursEntered === 0
  if (isRequestWithNoHours) {
    // Context-aware message based on duration type
    const noHoursMessage =
      selectedDuration === 'Full Day'
        ? ValidationMessages.NO_HOURS_ENTERED
        : ValidationMessages.NO_HOURS_ENTERED_PARTIAL_DAY
    errorMessages.push(noHoursMessage)
    fieldErrors['vacation'] = 'Required'
  }

  // Validate partial day times
  if (selectedDuration === 'Partial Day' && partialDayTimes) {
    validatePartialDayTimes(
      partialDayTimes.from,
      partialDayTimes.to,
      errorMessages
    )
    // Also validate that hours entered don't exceed the partial day duration
    validatePartialDayHoursDuration(
      partialDayTimes.from,
      partialDayTimes.to,
      normalizedHours,
      errorMessages,
      fieldErrors
    )
  }

  // Convert all fieldErrors to 'Revise' for inline display (except 'Required')
  Object.keys(fieldErrors).forEach((key) => {
    if (fieldErrors[key] !== 'Required') {
      fieldErrors[key] = 'Revise'
    }
  })

  return {
    nextFieldErrors: fieldErrors,
    messages: errorMessages.length ? errorMessages : null,
  }
}

export default { validateEntries }

export function validateVacationBiddingEntries(
  entries: Record<string, string> = {}
) {
  const fieldErrors: Record<string, string> = {}
  const vacationHours = toNum(entries.vacation)
  const errorMessages: string[] = []

  if (vacationHours > MAX_DURATION) {
    errorMessages.push(ValidationMessages.DURATION)
  }

  return {
    nextFieldErrors: fieldErrors,
    messages: errorMessages.length ? errorMessages : null,
  }
}

export function validateComments(
  comments?: string,
  isEducationRequest?: boolean
) {
  // Only validate comments if it's an education request
  if (!isEducationRequest) {
    return null
  }

  const commentIsEmpty = !comments?.trim()
  if (commentIsEmpty) {
    return ValidationMessages.EDUCATION_COMMENT_REQUIRED
  }
  return null
}

export function validateChoiceRank(selectedRank?: string | null) {
  const rankIsNotSelected = !selectedRank
  if (rankIsNotSelected) {
    return ValidationMessages.CHOICE_RANK_REQUIRED
  }
  return null
}

export function validateDateGaps(selectedDates: string[]): string | null {
  // Need at least 2 dates to check for gaps
  if (!selectedDates || selectedDates.length < 2) {
    return null
  }

  // Parse dates in MM/DD/YYYY format
  const parsedDates = selectedDates
    .map((dateStr) => {
      const parts = dateStr.split('/')
      if (parts.length !== 3) return null
      const month = Number.parseInt(parts[0], 10)
      const day = Number.parseInt(parts[1], 10)
      const year = Number.parseInt(parts[2], 10)
      return new Date(year, month - 1, day)
    })
    .filter((d): d is Date => d !== null && !Number.isNaN(d.getTime()))

  if (parsedDates.length < 2) {
    return null
  }
  // Sort dates for easier comparison
  parsedDates.sort((a, b) => a.getTime() - b.getTime())

  // Each date must be within 8 days of the highest date selected so far,
  // not the earliest overall — so a chain of dates can extend past 8 days total
  // as long as no single step exceeds the 8-day gap.
  for (let i = 0; i < parsedDates.length - 1; i++) {
    const firstDate = parsedDates[i]
    const secondDate = parsedDates[i + 1]

    const timeDiff = secondDate.getTime() - firstDate.getTime()
    const daysBetween = Math.floor(timeDiff / (1000 * 60 * 60 * 24))

    if (daysBetween > 8) {
      return ValidationMessages.REQUEST_DATES_EXCEED_8_DAY_GAP
    }
  }
  return null
}
